import { apiFetch } from "@d12labs/client";
import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@d12labs/ui/chart";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { KPI } from "@d12labs/ui/kpi";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, RefreshCw } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

type Summary = {
	today: { costUsd: number; requests: number };
	month: { costUsd: number; requests: number };
	byModel: { model: string; costUsd: number; requests: number }[];
	byUser: { userId: string | null; email: string | null; costUsd: number; requests: number }[];
	byFeature: { featureKey: string | null; costUsd: number; requests: number }[];
	dailySeries: { date: string; costUsd: number }[];
	dailyCapUsd: number | null;
};

// AI costs are often sub-dollar — show more precision when small.
const usd = (n: number) =>
	n > 0 && n < 1
		? `$${n.toFixed(4)}`
		: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const chartConfig = {
	costUsd: { label: "Spend", color: "var(--color-chart-1)" },
} satisfies ChartConfig;

export function AiUsagePage() {
	const usage = useQuery({
		queryKey: ["admin", "ai-usage"],
		queryFn: () => apiFetch<Summary>("/api/admin/ai-usage"),
		retry: false,
	});

	if (usage.error) {
		return (
			<>
				<PageHeader eyebrow="Admin" icon={BarChart3} title="AI usage" />
				<PageBody className="p-6">
					<p className="text-sm text-destructive">
						You don't have access to this page (set ADMIN_EMAILS to include your address).
					</p>
				</PageBody>
			</>
		);
	}

	const data = usage.data;
	const capPct =
		data?.dailyCapUsd && data.dailyCapUsd > 0
			? Math.min(100, Math.round((data.today.costUsd / data.dailyCapUsd) * 100))
			: null;
	const chartData = (data?.dailySeries ?? []).map((d) => ({
		label: new Date(`${d.date}T00:00:00Z`).toLocaleDateString(undefined, {
			month: "short",
			day: "numeric",
		}),
		costUsd: d.costUsd,
	}));

	return (
		<>
			<PageHeader
				eyebrow="Admin"
				icon={BarChart3}
				title="AI usage"
				subtitle="Spend recorded across every AI call in this deployment."
				badges={
					capPct != null ? (
						<Badge
							tone={capPct >= 100 ? "danger" : capPct >= 80 ? "warning" : "primary"}
							dot={
								capPct >= 100
									? "var(--color-danger)"
									: capPct >= 80
										? "var(--color-warning)"
										: "var(--color-primary)"
							}
						>
							{capPct}% of cap
						</Badge>
					) : undefined
				}
				actions={
					<Button
						variant="secondary"
						size="sm"
						onClick={() => usage.refetch()}
						disabled={usage.isFetching}
					>
						<RefreshCw className="size-3.5" />
						Refresh
					</Button>
				}
			/>
			<PageBody className="p-6">
				<div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
					{usage.isLoading || !data ? (
						<p className="text-sm text-muted-foreground">Loading…</p>
					) : (
						<>
							<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
								<KPI
									label="Spend · today"
									value={usd(data.today.costUsd)}
									sub={
										data.dailyCapUsd != null
											? `of ${usd(data.dailyCapUsd)} cap`
											: "No daily cap set"
									}
									accent={capPct != null && capPct >= 80 ? "text-warning" : undefined}
								/>
								<KPI
									label="Requests · today"
									value={String(data.today.requests)}
									sub="AI calls logged"
								/>
								<KPI label="Spend · MTD" value={usd(data.month.costUsd)} sub="this month" />
								<KPI
									label="Requests · MTD"
									value={String(data.month.requests)}
									sub={`${data.month.requests === 1 ? "request" : "requests"} this month`}
								/>
							</div>

							<section className="rounded-lg border border-hairline bg-surface-1 p-5">
								<h2 className="mb-3 card-title text-foreground">Last 30 days</h2>
								<ChartContainer config={chartConfig} className="aspect-[3/1] w-full">
									<BarChart data={chartData} margin={{ left: 4, right: 8, top: 8, bottom: 4 }}>
										<CartesianGrid vertical={false} />
										<XAxis
											dataKey="label"
											tickLine={false}
											axisLine={false}
											tick={{ fontSize: 11 }}
											interval="preserveStartEnd"
										/>
										<YAxis
											tickLine={false}
											axisLine={false}
											width={44}
											tick={{ fontSize: 11 }}
											tickFormatter={(v: number) => usd(v)}
										/>
										<ChartTooltip content={<ChartTooltipContent />} />
										<Bar
											dataKey="costUsd"
											fill="var(--color-costUsd)"
											radius={3}
											isAnimationActive={false}
										/>
									</BarChart>
								</ChartContainer>
							</section>

							<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
								<UsageTable
									title="By model"
									empty="No AI calls this month."
									rows={data.byModel.map((m) => ({
										key: m.model,
										label: m.model,
										mono: true,
										requests: m.requests,
										costUsd: m.costUsd,
									}))}
								/>
								<UsageTable
									title="By user"
									empty="No AI calls this month."
									rows={data.byUser.map((u) => ({
										key: u.userId ?? "system",
										label: u.email ?? "System / background",
										mono: false,
										requests: u.requests,
										costUsd: u.costUsd,
									}))}
								/>
							</div>
							<UsageTable
								title="By feature"
								empty="No AI calls this month."
								rows={data.byFeature.map((f) => ({
									key: f.featureKey ?? "untagged",
									label: f.featureKey ?? "Untagged",
									mono: true,
									requests: f.requests,
									costUsd: f.costUsd,
								}))}
							/>
						</>
					)}
				</div>
			</PageBody>
		</>
	);
}

function UsageTable({
	title,
	rows,
	empty,
}: {
	title: string;
	rows: { key: string; label: string; mono: boolean; requests: number; costUsd: number }[];
	empty: string;
}) {
	const columns = [
		{ label: "Name", width: "1fr" },
		{ label: "Requests", width: "90px", align: "right" as const },
		{ label: "Spend", width: "90px", align: "right" as const },
	];
	return (
		<div>
			<h2 className="mb-3 card-title text-foreground">{title}</h2>
			{rows.length === 0 ? (
				<p className="text-sm text-ink-subtle">{empty}</p>
			) : (
				<List>
					<ListHeader columns={columns} />
					{rows.map((r) => (
						<ListRow
							key={r.key}
							interactive={false}
							cells={[
								{
									value: r.label,
									width: "1fr",
									mono: r.mono,
									muted: r.mono,
								},
								{ value: r.requests, width: "90px", align: "right", muted: true },
								{ value: usd(r.costUsd), width: "90px", align: "right" },
							]}
						/>
					))}
				</List>
			)}
		</div>
	);
}
